<template>
  <v-dialog
      v-model="dialog"
      :width="dialogWidth"
      @keydown.enter.prevent="save"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title>
        <span class="headline">Firefly Config ({{ rules.name }})</span>
        <v-spacer/>
        <v-switch
            class="mb-5"
            v-model="model.enabled"
            label="Enabled"
            color="success"
            hide-details
        />
      </v-card-title>

      <v-card-text class="resizable-card__body">
        <v-col cols="4" sm="2" md="2">
          <v-btn @click="showPreview()" class="mr-2" small>View JSON</v-btn>
        </v-col>
        <v-row dense>
          <v-col cols="12" md="3" sm="3">
            <r-firefly-kind :model.sync="model.kind" label="Kind"/>
          </v-col>
          <v-col cols="12" md="2" sm="2">
            <r-range :model.sync="model.level" label="Level:"/>
          </v-col>
          <v-col cols="12" md="2" sm="2">
            <v-text-field label="Delay time:" v-model.number="model.delayTime" hide-details/>
          </v-col>
          <v-col cols="12" md="2" sm="2">
            <v-text-field label="CoolDown:" v-model.number="model.cooldownTime" hide-details/>
          </v-col>
          <v-col cols="12" md="2" sm="2">
            <v-text-field label="Reset time:" v-model.number="model.resetTime" hide-details/>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="6" sm="6">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    label="Max limit:"
                    v-bind="attrs"
                    v-on="on"
                    v-model="model.quantity"
                    hide-details
                />
              </template>
              <span>[2*D(6)+S]</span>
            </v-tooltip>
          </v-col>

          <v-col cols="12" md="6" sm="6">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    label="Count:"
                    v-bind="attrs"
                    v-on="on"
                    v-model="model.density"
                    hide-details
                />
              </template>
              <span>[2*D(6)+S]</span>
            </v-tooltip>
          </v-col>

          <v-col cols="12" md="6" sm="6">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    label="Interval:"
                    v-bind="attrs"
                    v-on="on"
                    v-model="model.interval"
                    hide-details
                />
              </template>
              <span>[2*D(6)+S]</span>
            </v-tooltip>
          </v-col>
          <v-col cols="12" sm="6" md="6" v-if="model.kind && model.kind.id === 9">
            <r-ad-placement-dropdown
                :model.sync="model.adPlacement"
                title="Ad Placement"
                hide-details
            />
          </v-col>
        </v-row>

        <v-row dense class="mt-4">
          <v-expansion-panels>
            <r-reward-panel :model.sync="model.reward" title="Reward"/>
          </v-expansion-panels>
        </v-row>
      </v-card-text>

      <v-card-actions class="resizable-card__actions">
        <v-spacer/>
        <v-btn @click="cancel" color="blue darken-1" text>
          Cancel
        </v-btn>
        <v-btn @click="save" color="blue darken-1" text>
          Save
        </v-btn>
      </v-card-actions>

      <div class="resize-handle" @mousedown="startResize"></div>
      <r-preview-dialog ref="preview-dialog"></r-preview-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import VueTypes from "vue-types";
import { ViewRules } from "@/common/ViewRules";
import { FireflyModelV2 } from "@/modules/redactor/models/firefly/FireflyModelV2";

import RFireflyKind from "@/modules/redactor/views/firefly/r-firefly-kind.vue";
import RAdPlacementDropdown from "@/modules/redactor/views/ad-settings/placement/r-ad-placement-dropdown.vue";
import RRange from "@/modules/redactor/views/utils/r-range.vue";
import RRewardPanel from "@/modules/redactor/views/reward/r-reward-panel.vue";
import RPreviewDialog from "@/modules/redactor/views/preview/r-preview-dialog.vue";
import {MapAddonGroupModel} from "@/modules/redactor/models/map-addon/core/MapAddonGroupModel";

export default {
  name: "r-firefly-v2-dialog",
  components: {RPreviewDialog, RRewardPanel, RRange, RAdPlacementDropdown, RFireflyKind },
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    model: VueTypes.objectOf(FireflyModelV2),
    rules: ViewRules.READONLY,
    dialogWidth: 747,
    dialogHeight: 546,
    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 747,
    resizeStartHeight: 546,
    viewportPadding: 32
  }),
  methods: {
    show(rules, model) {
      this.dialog = true;
      this.rules = rules;
      this.model = model || new FireflyModelV2();
      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    save() {
      if (this.model.valid()) {
        this.resolve && this.resolve(this.model);
        this.discard();
      } else {
        this.$alert("Invalid config!");
      }
    },
    cancel() {
      this.reject && this.reject();
      this.discard();
    },
    discard() {
      this.dialog = false;
      this.stopResize();
    },
    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;
      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },
    onResize(e) {
      if (!this.resizing) return;
      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;
      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;
      const minWidth = 520;
      const minHeight = 340;
      const maxWidth = window.innerWidth - this.viewportPadding;
      const maxHeight = window.innerHeight - this.viewportPadding;
      if (newWidth < minWidth) newWidth = minWidth;
      if (newWidth > maxWidth) newWidth = maxWidth;
      if (newHeight < minHeight) newHeight = minHeight;
      if (newHeight > maxHeight) newHeight = maxHeight;
      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },
    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;
      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    },
    showPreview() {
      this.$refs['preview-dialog'].show(this.rules, this.model, FireflyModelV2)
        .then((result) => {
          Object.assign(this.model, result);
        })
        .catch((e) => {
          this.$alert("Failed to parse config!" + e.message);
        });
    }
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.onResize);
    window.removeEventListener("mouseup", this.stopResize);
  }
};
</script>

<style scoped>
.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}
.resizable-card__body {
  flex: 1;
  overflow: auto;
}
.resizable-card__actions {
  flex-shrink: 0;
}
.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}
.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>